
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AddressFields } from "@/components/profile/AddressFields";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { BasicInfoFields } from "./components/BasicInfoFields";
import { CookingSpecialtiesField } from "./components/CookingSpecialtiesField";
import { SocialMediaFields } from "./components/SocialMediaFields";
import { CookingExperienceFields } from "./components/CookingExperienceFields";
import type { Database } from "@/integrations/supabase/types";

type Profile = Database['public']['Tables']['profiles']['Row'];

interface CookProfileData {
  id: string;
  created_at: string;
  updated_at: string;
  first_name: string | null;
  last_name: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  cooking_specialties: string | null;
  instagram_handle: string | null;
  tiktok_handle: string | null;
  facebook_handle: string | null;
  pinterest_handle: string | null;
  image_url: string | null;
  latitude: number | null;
  longitude: number | null;
  location_updated_at: string | null;
  allergy_considerations: string[] | null;
  chef_preferences: string[] | null;
  chef_rating: number | null;
  dietary_preferences: string[] | null;
  first_time_onboarding_completed: boolean | null;
  first_time_onboarding_completed_at: string | null;
  food_preferences: string[] | null;
  onboarding_completed: boolean | null;
  onboarding_completed_at: string | null;
  referral_source: string | null;
  years_experience: string;
  prep_methods: string[];
  kitchen_notes: string[];
  experience_types: string[];
  other_experience: string;
}

export const CookProfileForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<CookProfileData>({
    id: "",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    first_name: "",
    last_name: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    years_experience: "",
    prep_methods: [],
    kitchen_notes: [],
    experience_types: [],
    other_experience: "",
    cooking_specialties: "",
    instagram_handle: "",
    tiktok_handle: "",
    facebook_handle: "",
    pinterest_handle: "",
    image_url: null,
    latitude: null,
    longitude: null,
    location_updated_at: null,
    allergy_considerations: null,
    chef_preferences: null,
    chef_rating: null,
    dietary_preferences: null,
    first_time_onboarding_completed: null,
    first_time_onboarding_completed_at: null,
    food_preferences: null,
    onboarding_completed: null,
    onboarding_completed_at: null,
    referral_source: null
  });

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (data) {
        setProfile({
          ...profile,
          ...data,
          years_experience: data.years_experience || "",
          prep_methods: data.prep_methods || [],
          kitchen_notes: data.kitchen_notes || [],
          experience_types: data.experience_types || [],
          other_experience: data.other_experience || "",
          first_name: data.first_name || "",
          last_name: data.last_name || "",
          address: data.address || "",
          city: data.city || "",
          state: data.state || "",
          postal_code: data.postal_code || "",
          cooking_specialties: data.cooking_specialties || "",
          instagram_handle: data.instagram_handle || "",
          tiktok_handle: data.tiktok_handle || "",
          facebook_handle: data.facebook_handle || "",
          pinterest_handle: data.pinterest_handle || "",
        });
      }
    };

    fetchProfile();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update the profile with ALL fields, including cooking experience fields
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          first_name: profile.first_name,
          last_name: profile.last_name,
          address: profile.address,
          city: profile.city,
          state: profile.state,
          postal_code: profile.postal_code,
          cooking_specialties: profile.cooking_specialties,
          instagram_handle: profile.instagram_handle,
          tiktok_handle: profile.tiktok_handle,
          facebook_handle: profile.facebook_handle,
          pinterest_handle: profile.pinterest_handle,
          years_experience: profile.years_experience,
          prep_methods: profile.prep_methods,
          kitchen_notes: profile.kitchen_notes,
          experience_types: profile.experience_types,
          other_experience: profile.other_experience,
          updated_at: new Date().toISOString(),
        });

      if (profileError) throw profileError;

      const { data: existingVerification } = await supabase
        .from('cook_verifications')
        .select('*')
        .eq('cook_id', user.id)
        .maybeSingle();

      if (!existingVerification) {
        const { error: verificationError } = await supabase
          .from('cook_verifications')
          .insert({
            cook_id: user.id,
            status: 'in_progress'
          });

        if (verificationError) throw verificationError;
      }

      toast({
        title: "Profile updated",
        description: "Your profile has been saved successfully.",
      });

      navigate('/seller/verification/documents');
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileUpdate = (field: string, value: string | string[]) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <BasicInfoFields
          firstName={profile.first_name || ""}
          lastName={profile.last_name || ""}
          onFirstNameChange={(value) => handleProfileUpdate("first_name", value)}
          onLastNameChange={(value) => handleProfileUpdate("last_name", value)}
        />

        <AddressFields
          address={profile.address || ""}
          city={profile.city || ""}
          state={profile.state || ""}
          postalCode={profile.postal_code || ""}
          onAddressChange={(e) => handleProfileUpdate("address", e.target.value)}
          onCityChange={(e) => handleProfileUpdate("city", e.target.value)}
          onStateChange={(e) => handleProfileUpdate("state", e.target.value)}
          onPostalCodeChange={(e) => handleProfileUpdate("postal_code", e.target.value)}
          labelClassName="text-black"
        />

        <CookingExperienceFields
          yearsExperience={profile.years_experience}
          prepMethods={profile.prep_methods}
          kitchenNotes={profile.kitchen_notes}
          experienceTypes={profile.experience_types}
          otherExperience={profile.other_experience}
          onYearsExperienceChange={(value) => handleProfileUpdate("years_experience", value)}
          onPrepMethodsChange={(value) => handleProfileUpdate("prep_methods", value)}
          onKitchenNotesChange={(value) => handleProfileUpdate("kitchen_notes", value)}
          onExperienceTypesChange={(value) => handleProfileUpdate("experience_types", value)}
          onOtherExperienceChange={(value) => handleProfileUpdate("other_experience", value)}
        />

        <CookingSpecialtiesField
          specialties={profile.cooking_specialties || ""}
          onSpecialtiesChange={(value) => handleProfileUpdate("cooking_specialties", value)}
        />

        <SocialMediaFields
          instagram={profile.instagram_handle || ""}
          tiktok={profile.tiktok_handle || ""}
          facebook={profile.facebook_handle || ""}
          pinterest={profile.pinterest_handle || ""}
          onSocialMediaChange={handleProfileUpdate}
        />
      </div>

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading ? "Saving..." : "Continue to Document Verification"}
      </Button>
    </form>
  );
};
